import type { MapCoordinates } from "@b2bportal/core-types";
import { createQueryParams } from "@b2bportal/core-utilities";
import type { Suggestion } from "@b2bportal/ground-api";
import dayjs from "dayjs";
import { CarSortOptionEnum } from "../../types";
import { CAR_URL_PARAM_KEYS } from "../queryParams";
import { CarNavigationUtility } from ".";

export const URL_DATE_FORMAT = "YYYY-MM-DDTHH:mm" as const;

type BuildSearchParamsArgs = {
  pickupLocation: Suggestion;
  dropoffLocation: Suggestion;
  pickupTime: Date;
  dropoffTime: Date;
  driverAge: number;
};

function defined<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function formatDateForUrl(date: Date) {
  return dayjs(date).format(URL_DATE_FORMAT);
}

export const buildSearchParams = ({
  pickupLocation,
  dropoffLocation,
  pickupTime,
  dropoffTime,
  driverAge,
}: BuildSearchParamsArgs) => {
  const pickupLocationCode =
    CarNavigationUtility.placeSuggestionToUrl(pickupLocation);
  const dropoffLocationCode =
    CarNavigationUtility.placeSuggestionToUrl(dropoffLocation);
  const availabilityQuery = {
    [CAR_URL_PARAM_KEYS.PICKUP_LOCATION]: pickupLocationCode,
    [CAR_URL_PARAM_KEYS.DROPOFF_LOCATION]: dropoffLocationCode,
    [CAR_URL_PARAM_KEYS.PICKUP_TIME]: {
      value: pickupTime,
      type: "local",
    },
    [CAR_URL_PARAM_KEYS.DROPOFF_TIME]: {
      value: dropoffTime,
      type: "local",
    },
    [CAR_URL_PARAM_KEYS.DRIVER_AGE]: driverAge.toString(),
  } as const;
  return createQueryParams(availabilityQuery).toString();
};

const FUNNEL_SEARCH_PARAMS = [
  CAR_URL_PARAM_KEYS.PICKUP_LOCATION,
  CAR_URL_PARAM_KEYS.DROPOFF_LOCATION,
  CAR_URL_PARAM_KEYS.PICKUP_TIME,
  CAR_URL_PARAM_KEYS.DROPOFF_TIME,
  CAR_URL_PARAM_KEYS.DRIVER_AGE,
];

/**
 * Normalizes a cars availability path or query string into the search
 * parameters that define a shopping funnel. Filter/map/navigation-only params
 * (price range, latlng, sort, view, etc.) are excluded so changing them does
 * not rotate the funnel ID. Returns `undefined` when the query does not
 * describe a complete car search (e.g. an entry surface with no search).
 */
export const getCarFunnelSearch = (
  pathOrSearch: string | undefined
): Record<string, string> | undefined => {
  if (!pathOrSearch) {
    return undefined;
  }

  const queryStart = pathOrSearch.indexOf("?");
  const queryParams = new URLSearchParams(
    queryStart >= 0 ? pathOrSearch.slice(queryStart + 1) : pathOrSearch
  );

  const hasSearch =
    queryParams.has(CAR_URL_PARAM_KEYS.PICKUP_LOCATION) &&
    queryParams.has(CAR_URL_PARAM_KEYS.PICKUP_TIME) &&
    queryParams.has(CAR_URL_PARAM_KEYS.DROPOFF_TIME);

  if (!hasSearch) {
    return undefined;
  }

  return FUNNEL_SEARCH_PARAMS.reduce<Record<string, string>>((acc, key) => {
    const value = queryParams.get(key);
    if (value != null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

function initPriceRange(urlMin: number, urlMax: number) {
  const min = isNaN(urlMin) ? 0 : urlMin;
  const max = isNaN(urlMax) || urlMax === 0 ? Number.MAX_SAFE_INTEGER : urlMax;
  return {
    min,
    max,
    lowest: 0,
    highest: Number.MAX_SAFE_INTEGER,
  };
}

export enum ViewOption {
  LIST = "list",
  MAP = "map",
}

export function isValidSort(sort: unknown): sort is CarSortOptionEnum {
  return Object.values(CarSortOptionEnum).includes(sort as CarSortOptionEnum);
}

export function isValidView(sort: unknown): sort is ViewOption {
  return Object.values(ViewOption).includes(sort as ViewOption);
}

export type CarSearchQueryParamValues = {
  pickupDateTime: Date;
  dropoffDateTime: Date;
  driverAge: number;
  map?: {
    centroid: MapCoordinates | undefined;
    zoom: number;
  };
  filters: {
    priceRange: { min: number; max: number; lowest: number; highest: number };
  };
  sortBy: CarSortOptionEnum;
  view: ViewOption;
};

export const parseGroundParams = (
  searchParams: URLSearchParams
): CarSearchQueryParamValues => {
  const latlng =
    searchParams
      .get(CAR_URL_PARAM_KEYS.LAT_LNG)
      ?.split(",")
      .map(Number)
      .filter((n) => !Number.isNaN(n)) || [];
  const zoom = Number(searchParams.get(CAR_URL_PARAM_KEYS.ZOOM)) || 13;

  const filters = {
    priceRange: initPriceRange(
      Number(searchParams.get(CAR_URL_PARAM_KEYS.PRICE_RANGE_MIN)),
      Number(searchParams.get(CAR_URL_PARAM_KEYS.PRICE_RANGE_MAX))
    ),
  };

  const sortBy = searchParams.get(CAR_URL_PARAM_KEYS.SORT_BY);

  const view = searchParams.get(CAR_URL_PARAM_KEYS.VIEW);

  return {
    pickupDateTime: new Date(
      searchParams.get(CAR_URL_PARAM_KEYS.PICKUP_TIME) ?? ""
    ),
    dropoffDateTime: new Date(
      searchParams.get(CAR_URL_PARAM_KEYS.DROPOFF_TIME) ?? ""
    ),
    driverAge: Number(searchParams.get(CAR_URL_PARAM_KEYS.DRIVER_AGE)),
    map: {
      centroid:
        latlng.length > 1
          ? {
              lat: latlng[0] || 0,
              lng: latlng[1] || 0,
            }
          : undefined,
      zoom: zoom,
    },
    filters,
    sortBy: isValidSort(sortBy) ? sortBy : CarSortOptionEnum.Recommended,
    view: isValidView(view) ? view : ViewOption.LIST,
  };
};
